import { useCallback, useEffect, useRef, useState } from 'react'

const CHECK_INTERVAL = 60_000

/**
 * Polls /health every 60s. Captures the first version it sees, then
 * compares each subsequent reading. When the version changes (new
 * deploy went live), `updateAvailable` flips to true. The DefaultLayout
 * shell renders a "Refresh Now" banner at that point.
 *
 * Also returns the current `version` string for the footer.
 */
export function useVersionCheck() {
  const initialVersion = useRef<string | null>(null)
  const [version, setVersion] = useState<string | null>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/api/health?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      const data = await res.json()
      const v: string | undefined = data?.version
      if (!v) return
      if (initialVersion.current === null) {
        initialVersion.current = v
        setVersion(v)
      } else if (v !== initialVersion.current) {
        setVersion(v)
        setUpdateAvailable(true)
      }
    } catch {
      // network blips ignored — next poll will retry
    }
  }, [])

  useEffect(() => {
    check()
    const id = setInterval(check, CHECK_INTERVAL)
    return () => clearInterval(id)
  }, [check])

  const reload = useCallback(() => {
    window.location.reload()
  }, [])

  return { version, updateAvailable, reload }
}
